import { useTranslation } from "react-i18next";
import { useState, useMemo, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";

import { UserContext } from "../../hooks/UserContext";
import {
    NewTable,
    PageView,
    PageTopBar,
    Button,
    ConfirmDialog,
    Notification
} from "../../components";
import { Loader } from "../../components/Loader";
import { setShippingPackagesPageSize } from '../../redux/pageSizeSlice';
import {
    fetchShippingPackages,
    createShippingPackage,
    updateShippingPackage,
    deleteShippingPackage,
    restoreShippingPackage
} from "../../services/shippingPackageServices";
import { fetchWarehouse } from "../../services/warehouseSevices";
import { ShippingPackageFormModal } from "./ShippingPackageFormModal";
import { ShippingPackageHistoryModal } from "./ShippingPackageHistoryModal";
import { PencilIcon, TrashIcon, RefreshIcon, ClockIcon } from "@heroicons/react/outline";
import { Tooltip } from "antd";
import { PACKAGE_TYPES } from "./constants";
import { isSuperAdminUser } from "../../utils/userUtils";

export const ListShippingPackagesContainer = () => {
    const { i18n } = useTranslation();
    const { user } = useContext(UserContext);
    const { warehouse_id } = useParams();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const pageSize = useSelector((state) => state.pageSize.shippingPackagesList);

    // State
    const [searchParams, setSearchParams] = useState({
        page: 1,
        per_page: pageSize,
        warehouse_id: warehouse_id
    });
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [selectedHistoryPackage, setSelectedHistoryPackage] = useState(null);
    const [notification, setNotification] = useState({ show: false, type: '', title: '' });

    const isSuperAdmin = isSuperAdminUser(user);

    // Check if user can view shipping packages
    const hasAccess = !!isSuperAdmin || !!user?.permissions?.show_shipping_packages;

    // Check specific permissions
    const canCreate = !!isSuperAdmin || !!user?.permissions?.create_shipping_packages;
    const canUpdate = !!isSuperAdmin || !!user?.permissions?.update_shipping_packages;
    const canDelete = !!isSuperAdmin || !!user?.permissions?.delete_shipping_packages;
    const canManage = canCreate || canUpdate || canDelete;
    const canSeeCost = !!isSuperAdmin || !!user?.permissions?.cost_read_shipping_packages;

    // Fetch warehouse name
    const { data: warehouse } = useQuery(
        ['warehouse', warehouse_id],
        () => fetchWarehouse(warehouse_id),
        { keepPreviousData: true, refetchOnWindowFocus: false }
    );

    // Queries
    const {
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData
    } = useQuery(
        ['shipping_packages_list', searchParams],
        () => fetchShippingPackages(searchParams),
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            enabled: hasAccess
        }
    );

    // Mutations
    const createMutation = useMutation(createShippingPackage, {
        onSuccess: () => {
            queryClient.invalidateQueries(['shipping_packages_list']);
            setIsFormModalOpen(false);
            setSelectedPackage(null);
            showNotification('success', i18n.t('shipping_packages.package_created'));
        },
        onError: (error) => {
            showNotification('error', error?.response?.data?.error || i18n.t('shipping_packages.errors.create_error'));
        }
    });

    const updateMutation = useMutation(
        ({ id, data }) => updateShippingPackage(id, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['shipping_packages_list']);
                setIsFormModalOpen(false);
                setSelectedPackage(null);
                showNotification('success', i18n.t('shipping_packages.package_updated'));
            },
            onError: (error) => {
                showNotification('error', error?.response?.data?.error || i18n.t('shipping_packages.errors.update_error'));
            }
        }
    );

    const deleteMutation = useMutation(({ id, warehouse_id }) => deleteShippingPackage(id, { warehouse_id }), {
        onSuccess: () => {
            queryClient.invalidateQueries(['shipping_packages_list']);
            setIsDeleteModalOpen(false);
            setSelectedPackage(null);
            showNotification('success', i18n.t('shipping_packages.package_deleted'));
        },
        onError: (error) => {
            showNotification('error', error?.response?.data?.error || i18n.t('shipping_packages.errors.delete_error'));
        }
    });

    const restoreMutation = useMutation(({ id, warehouse_id }) => restoreShippingPackage(id, { warehouse_id }), {
        onSuccess: () => {
            queryClient.invalidateQueries(['shipping_packages_list']);
            setIsRestoreModalOpen(false);
            setSelectedPackage(null);
            showNotification('success', i18n.t('shipping_packages.package_restored'));
        },
        onError: (error) => {
            showNotification('error', error?.response?.data?.error || i18n.t('shipping_packages.errors.restore_error'));
        }
    });

    // Handlers
    const showNotification = (type, title) => {
        setNotification({ show: true, type, title });
        setTimeout(() => setNotification({ show: false, type: '', title: '' }), 3000);
    };

    const handleOpenCreateModal = () => {
        setSelectedPackage(null);
        setIsFormModalOpen(true);
    };

    const handleOpenEditModal = (pkg) => {
        setSelectedPackage(pkg);
        setIsFormModalOpen(true);
    };

    const handleOpenDeleteModal = (pkg) => {
        setSelectedPackage(pkg);
        setIsDeleteModalOpen(true);
    };

    const handleSubmitForm = (formData) => {
        if (selectedPackage) {
            updateMutation.mutate({ id: selectedPackage.id, data: { ...formData, warehouse_id: parseInt(warehouse_id) } });
        } else {
            createMutation.mutate({ ...formData, warehouse_id: parseInt(warehouse_id) });
        }
    };

    const handleConfirmDelete = () => {
        if (selectedPackage) {
            deleteMutation.mutate({ id: selectedPackage.id, warehouse_id: parseInt(warehouse_id) });
        }
    };

    const handleOpenRestoreModal = (pkg) => {
        setSelectedPackage(pkg);
        setIsRestoreModalOpen(true);
    };

    const handleConfirmRestore = () => {
        if (selectedPackage) {
            restoreMutation.mutate({ id: selectedPackage.id, warehouse_id: parseInt(warehouse_id) });
        }
    };

    const handleOpenHistory = (pkg) => {
        setSelectedHistoryPackage(pkg);
        setIsHistoryModalOpen(true);
    };

    const getPackageTypeLabel = (type) => {
        return i18n.t(`shipping_packages.package_types.${type}`) || type;
    };

    // Filter handlers
    const packageTypeFilters = useMemo(() => PACKAGE_TYPES.map(type => ({
        id: type.id,
        label: i18n.t(`shipping_packages.package_types.${type.id}`) || type.id
    })), [i18n]);

    const MUTUALLY_EXCLUSIVE_FILTERS = {
        'store_name': 'global_packages_only',
        'global_packages_only': 'store_name',
        'inactive_only': 'show_all',
        'show_all': 'inactive_only'
    };

    const updateFiltersData = (updatedData, filter) => {
        setFiltersData((prev) => {
            const opposite = MUTUALLY_EXCLUSIVE_FILTERS[filter] || null;
            return prev.map((item) => {
                if (item.key === filter) {
                    return { ...item, data: updatedData };
                }
                if (opposite && item.key === opposite && updatedData.visible) {
                    return { ...item, data: { ...item.data, visible: false, value: item.data.type === 'text' ? '' : undefined } };
                }
                return item;
            }).sort((a, b) => a.data.visible > b.data.visible ? -1 : 1);
        });
    };

    const handleApplyFilters = () => {
        const newSearchParams = {
            page: 1,
            per_page: pageSize,
            warehouse_id: warehouse_id
        };
        filtersData.forEach((filter) => {
            const isEmpty = Array.isArray(filter.data.value)
                ? filter.data.value.length === 0
                : !filter.data.value;
            if (filter.data.visible && !isEmpty) {
                // Map boolean filters to the active API param
                if (filter.key === 'inactive_only') {
                    newSearchParams['active'] = false;
                } else if (filter.key === 'show_all') {
                    newSearchParams['active'] = 'all';
                } else {
                    newSearchParams[filter.key] = filter.data.value;
                }
            }
        });
        setSearchParams(newSearchParams);
        queryClient.invalidateQueries(['shipping_packages_list']);
    };

    const handleResetFilters = () => {
        setSearchParams({
            page: 1,
            per_page: pageSize,
            warehouse_id: warehouse_id
        });
        setFiltersData(filtersInitialData);
    };

    const filtersInitialData = useMemo(() => [
        {
            key: "package_type",
            label: i18n.t("shipping_packages.filters.package_type"),
            data: {
                visible: false,
                type: 'multiFilter',
                value: [],
                placeholder: i18n.t("shipping_packages.filters.package_type"),
                options: packageTypeFilters
            }
        },
        {
            key: "name",
            label: i18n.t("shipping_packages.filters.name"),
            data: {
                visible: false,
                type: 'text',
                value: '',
                placeholder: i18n.t("shipping_packages.filters.name")
            }
        },
        {
            key: "bar_code",
            label: i18n.t("shipping_packages.filters.bar_code"),
            data: {
                visible: false,
                type: 'text',
                value: '',
                placeholder: i18n.t("shipping_packages.filters.bar_code")
            }
        },
        {
            key: "store_name",
            label: i18n.t("shipping_packages.filters.store_name"),
            data: {
                visible: false,
                type: 'text',
                value: '',
                placeholder: i18n.t("shipping_packages.filters.store_name")
            }
        },
        {
            key: "global_packages_only",
            label: i18n.t("shipping_packages.filters.global_packages_only"),
            data: {
                visible: false,
                type: 'boolean',
                onClickValue: true
            }
        },
        {
            key: "inactive_only",
            label: i18n.t("shipping_packages.filters.inactive_only"),
            data: {
                visible: false,
                type: 'boolean',
                onClickValue: true
            }
        },
        {
            key: "show_all",
            label: i18n.t("shipping_packages.filters.all"),
            data: {
                visible: false,
                type: 'boolean',
                onClickValue: true
            }
        }
    ], [packageTypeFilters, i18n]);

    const [filtersData, setFiltersData] = useState(filtersInitialData);

    // Sync filter labels when language changes, preserving user values/visibility
    useEffect(() => {
        setFiltersData((prev) =>
            filtersInitialData.map((initial) => {
                const existing = prev.find((f) => f.key === initial.key);
                if (!existing) return initial;
                return {
                    ...initial,
                    data: { ...initial.data, visible: existing.data.visible, value: existing.data.value }
                };
            })
        );
    }, [filtersInitialData]);

    // Table data
    const memoizedTableData = useMemo(() => {
        return (data?.shipping_packages || []).map((pkg) => {
            return {
                key: pkg.id,
                object: pkg,
                name: (
                    <div className="text-sm text-gray-900">
                        {pkg.name}
                    </div>
                ),
                dimensions: (
                    <div className="text-sm text-gray-500">
                        <div>{pkg.length_cm} x {pkg.width_cm} x {pkg.height_cm}</div>
                        <div className="text-xs text-gray-400">
                            {i18n.t('shipping_packages.headers.length').toLowerCase()} x {i18n.t('shipping_packages.headers.width').toLowerCase()} x {i18n.t('shipping_packages.headers.height').toLowerCase()} (cm)
                        </div>
                    </div>
                ),
                package_type: (
                    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getPackageTypeLabel(pkg.package_type)}
                    </div>
                ),
                store: (
                    <div className="text-sm text-gray-500">
                        {pkg.store?.name || '-'}
                    </div>
                ),
                bar_code: (
                    <div className="text-sm text-gray-500">
                        {pkg.bar_code || '-'}
                    </div>
                ),
                volume_liters: (
                    <div className="text-sm text-gray-500">
                        {pkg.volume_liters ? `${pkg.volume_liters} L` : '-'}
                    </div>
                ),
                weight_kg: (
                    <div className="text-sm text-gray-500">
                        {pkg.weight_kg != null ? `${pkg.weight_kg} kg` : '-'}
                    </div>
                ),
                volumetric_weight: (
                    <div className="text-sm text-gray-500">
                        {pkg.volumetric_weight} kg
                    </div>
                ),
                cost: (
                    <div className="text-sm text-gray-500">
                        {pkg.cost != null && pkg.cost !== '' ? `$${parseFloat(pkg.cost).toFixed(2)}` : '-'}
                    </div>
                ),
                actions: canManage ? (
                    <div className="flex space-x-2">
                        <Tooltip title={i18n.t('shipping_packages.history.view_history')}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenHistory(pkg);
                                }}
                                className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                            >
                                <ClockIcon className="h-5 w-5" />
                            </button>
                        </Tooltip>
                        {pkg.active === false ? (
                            canDelete && (
                                <Tooltip title={i18n.t('shipping_packages.restore_package')}>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenRestoreModal(pkg);
                                        }}
                                        className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                                    >
                                        <RefreshIcon className="h-5 w-5" />
                                    </button>
                                </Tooltip>
                            )
                        ) : (
                            <>
                                {canUpdate && (
                                    <Tooltip title={i18n.t('shipping_packages.edit_package')}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenEditModal(pkg);
                                            }}
                                            className="p-1 text-gray-500 hover:text-indigo-600 transition-colors"
                                        >
                                            <PencilIcon className="h-5 w-5" />
                                        </button>
                                    </Tooltip>
                                )}
                                {canDelete && (
                                    <Tooltip title={i18n.t('shipping_packages.delete_package')}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenDeleteModal(pkg);
                                            }}
                                            className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </div>
                ) : null
            };
        });
    }, [data?.shipping_packages, i18n, canManage, canUpdate, canDelete]);

    const memoizedColumns = useMemo(() => {
        const columns = [
            {
                Header: i18n.t("shipping_packages.headers.name"),
                accessor: "name",
                disableSortBy: true
            },
            {
                Header: i18n.t("shipping_packages.headers.dimensions"),
                accessor: "dimensions",
                disableSortBy: true
            },
            {
                Header: i18n.t("shipping_packages.headers.package_type"),
                accessor: "package_type",
                disableSortBy: true
            },
            {
                Header: i18n.t("shipping_packages.headers.store"),
                accessor: "store",
                disableSortBy: true
            },
            {
                Header: i18n.t("shipping_packages.headers.bar_code"),
                accessor: "bar_code",
                disableSortBy: true
            },
            {
                Header: i18n.t("shipping_packages.headers.volume_liters"),
                accessor: "volume_liters",
                disableSortBy: true
            },
            {
                Header: i18n.t("shipping_packages.headers.weight_kg"),
                accessor: "weight_kg",
                disableSortBy: true
            },
            {
                Header: i18n.t("shipping_packages.headers.volumetric_weight"),
                accessor: "volumetric_weight",
                disableSortBy: true
            },
        ];

        if (canSeeCost) {
            columns.push({
                Header: i18n.t("shipping_packages.headers.cost"),
                accessor: "cost",
                disableSortBy: true
            });
        }

        if (canManage) {
            columns.push({
                Header: i18n.t("shipping_packages.headers.actions"),
                accessor: "actions",
                disableSortBy: true
            });
        }

        return columns;
    }, [i18n, canManage, canSeeCost]);

    if (!hasAccess) {
        return (
            <PageView>
                <div className="text-center py-12">
                    <p className="text-gray-500">{i18n.t('shipping_packages.errors.no_access')}</p>
                </div>
            </PageView>
        );
    }

    return (
        <>
            <Notification
                show={notification.show}
                setShow={(val) => setNotification(prev => ({ ...prev, show: val }))}
                type={notification.type}
                title={notification.title}
            />

            <PageView
                topMenu={
                    <PageTopBar>
                        <div className="text-lg font-semibold">
                            {i18n.t('shipping_packages.title')}
                        </div>
                        {canCreate && (
                            <Button
                                type="primary"
                                onClick={handleOpenCreateModal}
                                className="px-4"
                            >
                                {i18n.t('shipping_packages.add_package')}
                            </Button>
                        )}
                    </PageTopBar>
                }
                childrenFullWidth={true}
                topMenuFullWidth={true}
            >
                {(!isPreviousData && isLoading) ? (
                    <Loader show={true} />
                ) : isError ? (
                    <div className="text-center py-12">
                        <p className="text-red-500">
                            {error?.message || i18n.t('shipping_packages.errors.fetch_error')}
                        </p>
                    </div>
                ) : (
                    <>
                        <NewTable
                        data={memoizedTableData}
                        columns={memoizedColumns}
                        showLoader={isFetching}
                        showPaginationOnFooter
                        isFetching={isFetching}
                        emptyTableText={i18n.t("shipping_packages.empty_table")}
                        paginationMeta={data?.meta}
                        onPaginationChange={(page) => {
                            setSearchParams((prev) => ({ ...prev, page: page }));
                        }}
                        onPageSizeChange={(page) => {
                            setSearchParams((prev) => ({ ...prev, per_page: page.id, page: 1 }));
                            dispatch(setShippingPackagesPageSize(page.id));
                        }}
                        onSearchHandler={updateFiltersData}
                        handleResetFilters={handleResetFilters}
                        filtersData={filtersData}
                        hasExport={false}
                        footerText={i18n.t("shipping_packages.footer_text")}
                        applyFiltersManually={true}
                        onManuallyAppliedFilters={handleApplyFilters}
                    />
                    </>
                )}
            </PageView>

            {/* Create/Edit Modal */}
            <ShippingPackageFormModal
                open={isFormModalOpen}
                setOpen={setIsFormModalOpen}
                packageData={selectedPackage}
                onSubmit={handleSubmitForm}
                isLoading={createMutation.isLoading || updateMutation.isLoading}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmDialog
                open={isDeleteModalOpen}
                setOpen={setIsDeleteModalOpen}
                title={i18n.t('shipping_packages.delete_package')}
                description={`${i18n.t('shipping_packages.confirm_delete')} ${i18n.t('shipping_packages.delete_warning')}`}
                confirmLabel={i18n.t('shipping_packages.delete_package')}
                cancelLabel={i18n.t('shipping_packages.form.cancel')}
                onConfirm={handleConfirmDelete}
                onCancel={() => { if (!deleteMutation.isLoading) setIsDeleteModalOpen(false); }}
                loading={deleteMutation.isLoading}
            />

            {/* Restore Confirmation Modal */}
            <ConfirmDialog
                open={isRestoreModalOpen}
                setOpen={setIsRestoreModalOpen}
                title={i18n.t('shipping_packages.restore_package')}
                description={`${i18n.t('shipping_packages.confirm_restore')} ${i18n.t('shipping_packages.restore_warning')}`}
                confirmLabel={i18n.t('shipping_packages.restore_package')}
                cancelLabel={i18n.t('shipping_packages.form.cancel')}
                onConfirm={handleConfirmRestore}
                onCancel={() => { if (!restoreMutation.isLoading) setIsRestoreModalOpen(false); }}
                loading={restoreMutation.isLoading}
            />

            {/* History Modal */}
            <ShippingPackageHistoryModal
                open={isHistoryModalOpen}
                setOpen={setIsHistoryModalOpen}
                packageData={selectedHistoryPackage}
                warehouseId={parseInt(warehouse_id)}
            />
        </>
    );
};
